import {
  Search,
  X,
  Building2,
  MapPin,
  LogOut,
  ChevronRight,
  Fingerprint,
} from 'lucide-react'
import debounce from 'lodash/debounce'
import { useEffect, useRef, useState } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'
import { useNavigate } from 'react-router'
import { Page } from 'tailwind-v4/blocks/page'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'tailwind-v4/components/table'
import { Button } from 'tailwind-v4/components/button'
import { Skeleton } from 'tailwind-v4/components/skeleton'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from 'tailwind-v4/components/input-group'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'tailwind-v4/components/toggle-group'
import { SupportVerificationCodeDialog } from '@/common/support-verification-code'
import useSelfServe from '@/components/self-serve/useSelfServe'
import { defaultServicePagination } from '@/connect-types/backend/service'
import { setInitialSearch } from '@/state/global/global.actions'
import { useAppDispatch, useAppSelector } from '@/state/helpers/useApp'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import { getFirst } from '@/utils/common'
import { useColorMode } from '@/hooks/use-color-mode'
import Profile from './organisations/profile'
import { PendingInvitationsSection } from './organisations/pending-invitations'
import { NoOrganisations } from './organisations/no-organisations'

export type SearchType = 'organisations' | 'venues'

function SkeletonRows({ cols }: { cols: number }) {
  return Array.from({ length: 6 }).map((_, r) => (
    <TableRow key={`skeleton-${r}`}>
      {Array.from({ length: cols }).map((__, c) => (
        <TableCell key={c}>
          <Skeleton className="h-4 w-full max-w-40" />
        </TableCell>
      ))}
    </TableRow>
  ))
}

function Organisations() {
  const navigate = useNavigate()
  const [supportCodeOpen, setSupportCodeOpen] = useState(false)
  useColorMode()
  const search = useAppSelector((state: any) => state.global.pagination.search)
  const dispatch = useAppDispatch()
  const [searchValue, setSearchValue] = useState<string>(search ?? '')
  const [searchType, setSearchType] = useQueryParam(
    'type',
    withDefault(StringParam, 'organisations')
  )
  const [cursor, setCursor] = useQueryParam(
    'cursor',
    withDefault(StringParam, '')
  )
  // Where to land inside the chosen organisation. Set by org-less deep links
  // (e.g. /knowledge-base → next=/venues?knowledge_base=true) when the user
  // has several organisations and we can't pick for them. Only a same-site
  // path is honoured, so a crafted link can't bounce someone off the app.
  const [nextParam] = useQueryParam('next', withDefault(StringParam, ''))
  const next =
    nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : ''
  const orgHref = (orgId: string) => `/${orgId}${next}`
  const [hasNavigated, setHasNavigated] = useState(false)

  const selfserve = useSelfServe()
  const isVenues = searchType === 'venues'

  const {
    data: organisationsData,
    isLoading: orgLoading,
    isSuccess,
  } = morpheusApi.useGetOrganisationsSearchQuery(
    { search, limit: 20 },
    { skip: isVenues }
  )

  const organisations = (organisationsData ?? { data: [] }) as {
    data: { id: string; name?: string; [key: string]: any }[]
  }

  // Unconditional probe for "does this account belong to ANY organisation?" —
  // the question the empty state below turns on, which the query above can't
  // answer while it's filtered by a search term or skipped on the venues tab.
  // Same args as that query whenever we're on the organisations tab with an
  // empty search, so RTK Query dedupes the two into a single request; it only
  // costs an extra call while searching or on the venues tab.
  const {
    data: anyOrganisationsData,
    isSuccess: anyOrganisationsLoaded,
    isFetching: anyOrganisationsFetching,
    refetch: refetchAnyOrganisations,
  } = morpheusApi.useGetOrganisationsSearchQuery({ search: '', limit: 20 })

  const { data: venues = defaultServicePagination, isLoading: venueLoading } =
    morpheusApi.useGetLocationsQuery(
      { limit: 20, cursor, search },
      { skip: !isVenues }
    )

  // Gates the single-organisation auto-redirect below: a user with a pending
  // invitation should land on this page to action it, not be swept straight
  // into their one existing org. `isSuccess` (not just `data`) so the
  // redirect waits for this query to settle rather than firing on the
  // pre-load `undefined` before we actually know there's nothing pending.
  const {
    data: pendingInvitations,
    isSuccess: pendingInvitationsLoaded,
    isFetching: pendingInvitationsFetching,
    refetch: refetchPendingInvitations,
  } = morpheusApi.useGetPendingInvitationsQuery()
  const hasPendingInvitations = (pendingInvitations?.length ?? 0) > 0

  useEffect(() => {
    if (!selfserve.data || hasNavigated) return
    if (!selfserve.data.backend?.organization_id) return
    setHasNavigated(true)
    navigate(`${selfserve.data.backend.organization_id}/self-serve`)
  }, [navigate, selfserve.data, hasNavigated])

  useEffect(() => {
    if (search) return
    if (!isSuccess) return
    if (!pendingInvitationsLoaded) return
    if (hasPendingInvitations) return
    if (!organisations.data || organisations.data.length !== 1) return
    const firstOrg = getFirst(organisations.data) as any
    if (firstOrg?.id) navigate(`/${firstOrg.id}${next}`)
  }, [
    organisations.data,
    navigate,
    next,
    search,
    isSuccess,
    pendingInvitationsLoaded,
    hasPendingInvitations,
  ])

  const delayedSearchQuery = useRef(
    debounce((term: string) => {
      setCursor('')
      dispatch(setInitialSearch(term))
    }, 400)
  ).current

  const onSearchChange = (value: string) => {
    setSearchValue(value)
    delayedSearchQuery(value)
  }

  const clearSearch = () => {
    setSearchValue('')
    delayedSearchQuery('')
  }

  // Cmd/Ctrl+K focuses the search field.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        document.getElementById('org-search')?.focus()
      }
    }
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
    }
  }, [])

  const venueLinks =
    isVenues && venues && typeof venues === 'object' && 'links' in venues
      ? (venues as any).links
      : null

  const orgRows = organisations.data
  const venueRows = (venues as any).data ?? []
  const orgEmpty = !orgLoading && orgRows.length === 0
  const venueEmpty = !venueLoading && venueRows.length === 0

  // A brand-new signup: zero organisations, nothing pending to action. Only
  // from settled signals — an unsettled query here would flash the onboarding
  // state over a perfectly normal account on every load. Note `search` is
  // deliberately not part of this: with no organisations there is nothing to
  // search, and the search box isn't rendered in this branch anyway.
  // (`useSelfServe`'s redirect is left alone — a self-serve signup HAS an
  // organisation, so the probe finds it, and `hasNavigated` covers the rest.)
  const accountEmpty =
    anyOrganisationsLoaded &&
    (anyOrganisationsData?.data?.length ?? 0) === 0 &&
    pendingInvitationsLoaded &&
    !hasPendingInvitations &&
    !hasNavigated

  const refreshAccount = () => {
    refetchAnyOrganisations()
    refetchPendingInvitations()
  }

  const toolbar = (
    <>
      <InputGroup className="h-9 flex-1">
        <InputGroupAddon>
          <Search className="size-4 text-muted-foreground" />
        </InputGroupAddon>
        <InputGroupInput
          id="org-search"
          autoFocus
          value={searchValue}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          placeholder={`Search ${searchType}…`}
        />
        {searchValue ? (
          <InputGroupAddon align="inline-end">
            <InputGroupButton aria-label="Clear search" onClick={clearSearch}>
              <X className="size-4" />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      <ToggleGroup
        variant="outline"
        size="lg"
        value={[searchType]}
        onValueChange={(groupValue: unknown[]) => {
          const next = groupValue[0]
          if (typeof next === 'string') {
            setCursor('')
            setSearchType(next)
          }
        }}
      >
        <ToggleGroupItem value="organisations" aria-label="Organisations">
          <Building2 className="size-4" />
          <span className="hidden sm:inline">Organisations</span>
        </ToggleGroupItem>
        <ToggleGroupItem value="venues" aria-label="Venues">
          <MapPin className="size-4" />
          <span className="hidden sm:inline">Venues</span>
        </ToggleGroupItem>
      </ToggleGroup>
    </>
  )

  const header = (
    <header className="flex h-14 flex-shrink-0 items-center border-b border-border px-4">
      <div className="mx-auto flex w-full max-w-4xl items-center justify-between">
        <img
          src="/stampede-black-full.png"
          alt="Stampede"
          className="h-6 w-auto dark:hidden"
        />
        <img
          src="/stampede-white-full.png"
          alt="Stampede"
          className="hidden h-6 w-auto dark:block"
        />
        <div className="flex items-center gap-2">
          <Profile />
          <SupportVerificationCodeDialog
            open={supportCodeOpen}
            onOpenChange={setSupportCodeOpen}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Verify my identity"
            title="Verify my identity"
            onClick={() => {
              setSupportCodeOpen(true)
            }}
          >
            <Fingerprint />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Log out"
            title="Log out"
            onClick={() => navigate('oauth/logout')}
          >
            <LogOut />
          </Button>
        </div>
      </div>
    </header>
  )

  // A brand-new account gets its own body: nothing to search and nothing to
  // list, so the toolbar and the table both go and the guidance IS the page.
  // An early return rather than a ternary around the list below, which has
  // branches of its own.
  if (accountEmpty) {
    return (
      <div className="new-ui-theme flex h-screen flex-col overflow-hidden bg-background text-foreground">
        {header}
        <div className="min-h-0 flex-1">
          <Page
            // Top-aligned, not centred: the content column just wraps the
            // guidance and sits at the top of the scroller. No floating Menu
            // pill on this page, so the mobile bottom clearance would only add
            // dead scroll below it.
            contentClassName="flex flex-col"
            mobileBottomClearance={false}
          >
            <NoOrganisations
              onRefresh={refreshAccount}
              isRefreshing={
                anyOrganisationsFetching || pendingInvitationsFetching
              }
            />
          </Page>
        </div>
      </div>
    )
  }

  return (
    <div className="new-ui-theme flex h-screen flex-col overflow-hidden bg-background text-foreground">
      {header}

      {/* Body */}
      <div className="min-h-0 flex-1">
        <Page bleed toolbar={toolbar}>
          <div className="flex flex-col gap-6">
            {/* `bleed` strips the mobile gutter so the table reaches the screen
              edge — this bordered list has to put its own back. */}
            <PendingInvitationsSection className="px-4 @2xl:px-0" />
            {isVenues ? (
              <Table
                pagination={{
                  type: 'cursor',
                  hasPrev: Boolean(venueLinks?.prev),
                  hasNext: Boolean(venueLinks?.next),
                  onPrev: () => {
                    if (venueLinks?.prev) setCursor(venueLinks.prev)
                  },
                  onNext: () => {
                    if (venueLinks?.next) setCursor(venueLinks.next)
                  },
                  label: 'Venues',
                }}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>Venue</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {venueLoading && venueRows.length === 0 ? (
                    <SkeletonRows cols={3} />
                  ) : venueEmpty ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="py-10 text-center text-muted-foreground"
                      >
                        No venues found
                      </TableCell>
                    </TableRow>
                  ) : (
                    venueRows.map((venue: any) => (
                      <TableRow
                        key={venue.id}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/${venue.organization_id}/venues/${venue.serial}`
                          )
                        }
                      >
                        <TableCell className="font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                              <MapPin className="size-3.5" />
                            </span>
                            <span className="truncate">
                              {venue.alias || venue.serial}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground tabular-nums">
                          {venue.serial}
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgLoading && orgRows.length === 0 ? (
                    <SkeletonRows cols={2} />
                  ) : orgEmpty ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        className="py-10 text-center text-muted-foreground"
                      >
                        No organisations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    orgRows.map((org) => (
                      <TableRow
                        key={org.id}
                        className="cursor-pointer"
                        onClick={() => navigate(orgHref(org.id))}
                      >
                        <TableCell className="font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                              <Building2 className="size-3.5" />
                            </span>
                            <span className="truncate">{org.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </Page>
      </div>
    </div>
  )
}

export default Organisations
